import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "../lib/api";
import { BadgeCheck, Flame, Target, TrendingUp, TrendingDown, ArrowLeft } from "lucide-react";
import TraderBadge from "../components/TraderBadge";
import BadgeInfoPanel from "../components/BadgeInfoPanel";



function ChampionshipsPanel({ userId }) {
  const [wins, setWins] = useState([]);

  useEffect(() => {
    api.get("/monthly-challenge/champions/" + userId).then((r) => setWins(r.data || [])).catch(() => {});
  }, [userId]);

  if (wins.length === 0) return null;

  return (
    <div className="bg-[var(--bg-panel)] border border-[var(--border-soft)] p-6">
      <h3 className="font-mono text-xs uppercase tracking-widest text-[var(--text-tertiary)] mb-4">🏆 Monthly Championships</h3>
      <div className="space-y-3">
        {wins.map((w) => (
          <div key={w.id} className="text-sm">
            <div className="text-[var(--text-primary)] font-bold">{new Date(w.month + "-01").toLocaleDateString("en-US", { month: "long", year: "numeric" })}</div>
            <div className="text-green-400 font-mono text-xs">+{w.return_pct}% return</div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function PublicProfile() {  const { userId } = useParams();
  const [user, setUser] = useState(null);
  const [stats, setStats] = useState(null);
  const [activity, setActivity] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    setLoading(true);
    setError(false);
    Promise.all([
      api.get("/users/" + userId + "/public"),
      api.get("/community/predictions/user/" + userId),
      api.get("/users/" + userId + "/activity-stats"),
    ])
      .then(([u, s, a]) => {
        setUser(u.data);
        setStats(s.data);
        setActivity(a.data);
      })
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, [userId]);


  const bestCall = stats?.predictions?.find((p) => p.resolved && p.correct);

  if (loading) return <div className="pt-32 text-center text-[var(--text-tertiary)] font-mono">Loading profile...</div>;
  if (error || !user) return <div className="pt-32 text-center text-[var(--text-tertiary)] font-mono">User not found.</div>;

  return (
    <div className="pt-10 pb-20 max-w-[1400px] mx-auto px-6">
      <Link to="/community" className="inline-flex items-center gap-2 text-sm font-mono text-[var(--text-tertiary)] hover:text-yellow-400 transition mb-8">
        <ArrowLeft className="w-4 h-4" /> Back to community
      </Link>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        <aside className="lg:col-span-3 space-y-5">
          <BadgeInfoPanel />
        </aside>

        <div className="lg:col-span-6">
          <div className="bg-[var(--bg-panel)] border border-[var(--border-soft)] p-8">
            <div className="flex items-center gap-5">
              {user.avatar_url ? (
                <img src={user.avatar_url} alt={user.name} className="w-20 h-20 rounded-full object-cover border border-[var(--border-soft)]" />
              ) : (
                <div className="w-20 h-20 rounded-full bg-yellow-400 text-black font-bold flex items-center justify-center text-3xl">
                  {(user.name || "U")[0].toUpperCase()}
                </div>
              )}
              <div>
                <h1 className="font-display font-bold text-3xl text-[var(--text-primary)]">{user.name}</h1>
                <div className="flex items-center gap-2 flex-wrap mt-1">
                  {user.role === "admin" && (
                    <span className="text-xs font-mono uppercase tracking-widest text-yellow-400 bg-yellow-400/10 border border-yellow-400/30 px-2 py-0.5">
                      Admin
                    </span>
                  )}
                  <TraderBadge userId={userId} />
                </div>
                <div className="text-xs font-mono text-[var(--text-tertiary)] mt-2">
                  Joined {new Date(user.created_at).toLocaleDateString("en-US", { year: "numeric", month: "long" })}
                </div>
              </div>
            </div>

            {user.bio && (
              <p className="mt-5 text-[var(--text-secondary)] leading-relaxed">{user.bio}</p>
            )}

            {activity && (
              <div className="mt-6 pt-6 border-t border-[var(--border-soft)] grid grid-cols-3 gap-4">
                <div className="text-center">
                  <div className="font-display font-bold text-xl text-[var(--text-primary)]">{activity.posts_count}</div>
                  <div className="text-xs font-mono text-[var(--text-tertiary)] uppercase mt-1">Posts</div>
                </div>
                <div className="text-center">
                  <div className="font-display font-bold text-xl text-[var(--text-primary)]">{activity.comments_count}</div>
                  <div className="text-xs font-mono text-[var(--text-tertiary)] uppercase mt-1">Comments</div>
                </div>
                <div className="text-center">
                  <div className="font-display font-bold text-xl text-[var(--text-primary)]">{activity.weekly_played}</div>
                  <div className="text-xs font-mono text-[var(--text-tertiary)] uppercase mt-1">Weekly Calls</div>
                </div>
              </div>
            )}
          </div>

          {bestCall && (
            <div className="mt-6 bg-[var(--bg-panel)] border border-green-400/30 p-6">
              <div className="text-xs font-mono uppercase tracking-widest text-green-400 mb-2">🎯 Best Call</div>
              <Link to={"/community/" + bestCall.id} className="text-[var(--text-primary)] font-medium hover:text-yellow-400 transition">
                {bestCall.asset} {bestCall.direction} ${Number(bestCall.target_price).toLocaleString()} — correct
              </Link>
            </div>
          )}

          {stats && stats.total > 0 && (
            <div className="mt-6 bg-[var(--bg-panel)] border border-[var(--border-soft)] p-8">
              <h2 className="font-display font-bold text-xl text-[var(--text-primary)] mb-6 flex items-center gap-2">
                <Target className="w-5 h-5 text-yellow-400" /> Prediction Track Record
              </h2>

              <div className="flex items-center gap-2 flex-wrap mb-6">
                {stats.verified && (
                  <span className="flex items-center gap-1 text-xs font-mono px-2 py-1 border border-yellow-400/40 bg-yellow-400/10 text-yellow-400">
                    <BadgeCheck className="w-3 h-3" /> Verified Analyst
                  </span>
                )}
                {stats.streak >= 3 && (
                  <span className="flex items-center gap-1 text-xs font-mono px-2 py-1 border border-orange-400/40 bg-orange-400/10 text-orange-400">
                    <Flame className="w-3 h-3" /> {stats.streak} Win Streak
                  </span>
                )}
              </div>

              <div className="grid grid-cols-3 gap-4 mb-8">
                <div className="text-center">
                  <div className={"font-display font-bold text-3xl " +
                    (stats.accuracy >= 60 ? "text-green-400" : stats.accuracy >= 40 ? "text-yellow-400" : "text-red-400")}>
                    {stats.total === 0 || stats.correct === 0 ? "0%" : stats.accuracy + "%"}
                  </div>
                  <div className="text-xs font-mono text-[var(--text-tertiary)] uppercase mt-1">Accuracy</div>
                </div>
                <div className="text-center">
                  <div className="font-display font-bold text-3xl text-green-400">{stats.correct}</div>
                  <div className="text-xs font-mono text-[var(--text-tertiary)] uppercase mt-1">Correct</div>
                </div>
                <div className="text-center">
                  <div className="font-display font-bold text-3xl text-[var(--text-primary)]">{stats.total}</div>
                  <div className="text-xs font-mono text-[var(--text-tertiary)] uppercase mt-1">Total Calls</div>
                </div>
              </div>

              <div className="space-y-3">
                {stats.predictions.map((p) => {
                  const isBull = p.direction === "above";
                  return (
                    <Link key={p.id} to={"/community/" + p.id} className="block border border-[var(--border-soft)] hover:border-yellow-400/30 transition p-4">
                      <div className="flex items-center justify-between gap-3 flex-wrap">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className={"flex items-center gap-1 text-xs font-mono font-bold " + (isBull ? "text-green-400" : "text-red-400")}>
                            {isBull ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
                            {p.asset} {p.direction} ${Number(p.target_price).toLocaleString()}
                          </span>
                          <span className="text-xs font-mono text-[var(--text-tertiary)]">
                            by {new Date(p.target_date).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
                          </span>
                        </div>
                        {p.resolved ? (
                          <span className={"text-xs font-mono font-bold px-2 py-1 border " +
                            (p.correct ? "text-green-400 border-green-400/30 bg-green-400/10" : "text-red-400 border-red-400/30 bg-red-400/10")}>
                            {p.correct ? "✓ Correct" : "✗ Wrong"}
                          </span>
                        ) : (
                          <span className="text-xs font-mono text-yellow-400 border border-yellow-400/30 bg-yellow-400/5 px-2 py-1">
                            ⏳ Pending
                          </span>
                        )}
                      </div>
                      <div className="text-sm text-[var(--text-secondary)] mt-2 line-clamp-1">{p.title}</div>
                    </Link>
                  );
                })}
              </div>
            </div>
          )}

          {stats && stats.total === 0 && (
            <div className="mt-6 bg-[var(--bg-panel)] border border-[var(--border-soft)] p-8 text-center text-[var(--text-tertiary)] font-mono text-sm">
              No predictions posted yet.
            </div>
          )}
        </div>

        <aside className="lg:col-span-3 space-y-5">
          <ChampionshipsPanel userId={userId} />
        </aside>
      </div>
    </div>
  );
}